import React, { useEffect, useState } from "react";
import { FileText, Download, Building2, TrendingUp, Nfc } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { Label, scoreColor, EmptyState } from "../../../components/common";
import { Button } from "../../../components/ui/button";

const RANGES = [
  { v: 7, label: "7 days" },
  { v: 30, label: "30 days" },
  { v: 90, label: "90 days" },
];

export default function PlatformReports() {
  const [days, setDays] = useState(30);
  const [a, setA] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { api.get(`/platform/analytics?days=${days}`).then((r) => setA(r.data)).catch(() => {}); }, [days]);
  useEffect(() => { api.get("/platform/organizations").then((r) => setOrgs(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/platform/analytics.csv?days=${days}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `servantage_group_report_${days}d.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error(apiError(e)); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Label>Servantage Platform</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Group Reports</h1></div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 bg-white p-1" data-testid="report-range-switch">
            {RANGES.map((r) => (
              <button key={r.v} onClick={() => setDays(r.v)} data-testid={`report-range-${r.v}`}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${days === r.v ? "bg-[#4338CA] text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <Button onClick={exportCsv} disabled={downloading} data-testid="export-csv" className="bg-[#4338CA] hover:bg-[#3730A3]">
            <Download className="mr-1 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Per-corporate summary */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /><Label>Per-corporate summary</Label></div>
        {orgs.length === 0 ? <div className="mt-2"><EmptyState icon={FileText} title="No corporates yet" /></div> : (
          <div className="mt-3 overflow-x-auto sv-scroll">
            <table className="w-full min-w-[720px] text-sm" data-testid="report-org-table">
              <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2">Corporate</th><th>Industry</th><th>Plan</th><th>MRR</th><th>Service Index</th><th>Interactions</th><th>Locations</th><th>Employees</th><th>Open recovery</th><th>Status</th></tr></thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100" data-testid={`report-org-${o.id}`}>
                    <td className="py-3 font-semibold text-slate-800">{o.name}</td>
                    <td className="text-slate-500">{o.industry || "—"}</td>
                    <td className="text-slate-500">{o.plan || "—"}</td>
                    <td className="sv-metric text-slate-700">R{(o.mrr || 0).toLocaleString()}</td>
                    <td><span className="sv-metric font-bold" style={{ color: scoreColor(o.ssi) }}>{o.ssi.toFixed(2)}</span></td>
                    <td className="text-slate-500">{o.verified_interactions}</td>
                    <td className="text-slate-500">{o.locations}</td>
                    <td className="text-slate-500">{o.employees}</td>
                    <td className="text-slate-500">{o.open_recovery}</td>
                    <td><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${o.status === "suspended" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>{o.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Breakdown tables */}
      {a && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-slate-400" /><Label>Industry breakdown</Label></div>
            <div className="mt-3 overflow-x-auto sv-scroll">
              <table className="w-full min-w-[420px] text-sm" data-testid="report-industry-table">
                <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2">Industry</th><th>Corp</th><th>Emp</th><th>Interactions</th><th>Avg SSI</th></tr></thead>
                <tbody>
                  {a.industry_breakdown.map((i) => (
                    <tr key={i.industry} className="border-b border-slate-100">
                      <td className="py-2.5 font-semibold text-slate-800">{i.industry}</td>
                      <td className="text-slate-500">{i.orgs}</td>
                      <td className="text-slate-500">{i.employees}</td>
                      <td className="text-slate-500">{i.interactions}</td>
                      <td><span className="sv-metric font-bold" style={{ color: scoreColor(i.avg_ssi) }}>{i.avg_ssi.toFixed(2)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /><Label>Tier distribution</Label></div>
            <div className="mt-3 space-y-2">
              {a.tier_distribution.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No active subscriptions.</p> :
                a.tier_distribution.map((t) => (
                  <div key={t.plan} className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
                    <span className="font-semibold text-slate-700">{t.plan}</span>
                    <span className="text-sm text-slate-500">{t.count} corp · <b className="text-slate-700">R{t.mrr.toLocaleString()}/mo</b></span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Scan mix per corporate */}
      {a && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2"><Nfc className="h-4 w-4 text-slate-400" /><Label>NFC adoption by corporate · last {days} days</Label></div>
          {(!a.org_scan_mix || a.org_scan_mix.length === 0) ? (
            <p className="mt-3 py-6 text-center text-sm text-slate-400">No scans captured in this period yet.</p>
          ) : (
            <div className="mt-3 space-y-3" data-testid="scan-mix-table">
              {a.org_scan_mix.map((o) => (
                <div key={o.org} data-testid={`scan-mix-${o.org}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-800">{o.org}</span>
                    <span className="text-slate-500"><b className="text-[#4338CA]">{o.nfc_pct}% NFC</b> · {o.nfc} NFC / {o.qr} QR</span>
                  </div>
                  <div className="mt-1 flex h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full bg-[#4338CA]" style={{ width: `${o.nfc_pct}%` }} />
                    <div className="h-full bg-slate-300" style={{ width: `${100 - o.nfc_pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
